<template>
  <div class="max-w-2xl mx-auto px-4 py-6 sm:px-6 lg:px-8 space-y-6">
    <div class="flex items-center justify-between">
      <button @click="goBack" class="btn btn-secondary !p-2 !rounded-full">
        <span class="material-symbols-outlined">arrow_back</span>
      </button>
      <h1 class="text-xl font-bold text-slate-800 dark:text-slate-100">
        {{ isEditing ? 'Editar Gasto' : 'Adicionar Gasto' }}
      </h1>
      <button @click="handleSubmit" :disabled="isSubmitting" class="btn btn-primary min-w-[80px]">
        <span v-if="isSubmitting" class="material-symbols-outlined animate-spin text-lg">progress_activity</span>
        <span v-else>Salvar</span>
      </button>
    </div>

    <div class="panel p-5 sm:p-6">
      <form @submit.prevent="handleSubmit" class="space-y-6">
        <div>
          <label for="gDesc" class="form-label">Descrição do Gasto</label>
          <input id="gDesc" v-model="form.descricao" type="text" required placeholder="Ex: Cimento, Pisos, etc." class="form-input" />
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-6">
          <div>
            <label for="gValor" class="form-label">Valor (R$)</label>
            <input id="gValor" v-model.number="form.valor" type="number" step="0.01" required placeholder="500.00" class="form-input" />
          </div>
          <div>
            <label for="gCat" class="form-label">Categoria</label>
            <input id="gCat" v-model="form.categoria" type="text" placeholder="Ex: Material, Mão de Obra" class="form-input" />
          </div>
        </div>
        <div>
          <label for="gData" class="form-label">Data</label>
          <input id="gData" v-model="form.data" type="date" required class="form-input" />
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import axios from 'axios'

const router = useRouter()
const route = useRoute()

const isEditing = computed(() => !!route.params.gastoId)
const isSubmitting = ref(false)

const form = ref({
  descricao: '',
  valor: null,
  categoria: '',
  data: new Date().toISOString().split('T')[0]
})

const API_BASE_URL = 'https://backend.controlease.com.br/api'
const authConfig = () => {
  const token = localStorage.getItem('token')
  const headers = {}
  if (token) headers.Authorization = `Bearer ${token}`
  return { headers }
}

const fillForm = (data) => {
  form.value = {
    descricao: data?.descricao ?? '',
    valor: Number(data?.valor ?? 0),
    categoria: data?.categoria ?? '',
    data: (data?.data || '').slice(0, 10) || new Date().toISOString().split('T')[0]
  }
}

const fetchFromOrcamentoAndPick = async (orcamentoUuid, gastoId) => {
  const { data } = await axios.get(`${API_BASE_URL}/orcamentos-projeto/${orcamentoUuid}`, authConfig())
  const gasto = (data?.gastos || []).find(g => String(g.id) === String(gastoId))
  if (!gasto) {
    alert('Gasto não encontrado no projeto.')
    goBack()
    return
  }
  fillForm(gasto)
}

const fetchGasto = async () => {
  if (!isEditing.value) return
  const { orcamentoUuid, gastoId } = route.params
  if (!orcamentoUuid || !gastoId) return
  try {
    const { data } = await axios.get(`${API_BASE_URL}/orcamentos-projeto/${orcamentoUuid}/gastos/${gastoId}`, authConfig())
    fillForm(data)
  } catch (e) {
    const s = e.response?.status
    if (s === 401) {
      alert('Sessão expirada. Faça login novamente.')
      router.push({ name: 'Login' })
      return
    }
    try {
      await fetchFromOrcamentoAndPick(orcamentoUuid, gastoId)
    } catch (e2) {
      const s2 = e2.response?.status
      if (s2 === 401) {
        alert('Sessão expirada. Faça login novamente.')
        router.push({ name: 'Login' })
      } else {
        alert(e2.response?.data?.message || 'Erro ao carregar dados do gasto.')
        goBack()
      }
    }
  }
}

const handleSubmit = async () => {
  if (isSubmitting.value) return
  isSubmitting.value = true
  try {
    const { orcamentoUuid, gastoId } = route.params
    if (!orcamentoUuid) throw new Error('UUID do orçamento não encontrado.')
    const payload = { ...form.value, valor: Number(form.value.valor) }
    if (isEditing.value) {
      try {
        await axios.put(`${API_BASE_URL}/orcamentos-projeto/${orcamentoUuid}/gastos/${gastoId}`, payload, authConfig())
      } catch (e) {
        if (e.response?.status === 401) throw e
        await axios.put(`${API_BASE_URL}/gastos/${gastoId}`, payload, authConfig())
      }
    } else {
      await axios.post(`${API_BASE_URL}/orcamentos-projeto/${orcamentoUuid}/gastos`, payload, authConfig())
    }
    router.push({ name: 'OrcamentoProjeto', params: { id: orcamentoUuid } })
  } catch (error) {
    const s = error.response?.status
    if (s === 401) {
      alert('Sessão expirada. Faça login novamente.')
      router.push({ name: 'Login' })
    } else {
      alert(error.response?.data?.message || 'Erro ao salvar gasto.')
    }
  } finally {
    isSubmitting.value = false
  }
}

const goBack = () => {
  const { orcamentoUuid } = route.params
  if (orcamentoUuid) router.push({ name: 'OrcamentoProjeto', params: { id: orcamentoUuid } })
  else router.push('/user/orcamento-projeto')
}

onMounted(() => {
  if (isEditing.value) fetchGasto()
})
</script>

<style scoped>
.panel { @apply bg-white/70 dark:bg-brand-800/60 backdrop-blur-lg ring-1 ring-black/5 dark:ring-white/10 rounded-2xl shadow-xl shadow-slate-200/30 dark:shadow-black/20; }
.btn { @apply inline-flex items-center justify-center font-semibold py-2.5 px-5 rounded-xl transition-all duration-200 text-sm active:scale-95 disabled:opacity-60; }
.btn-primary { @apply btn bg-brand-600 hover:bg-brand-700 text-white shadow-lg shadow-brand-500/20; }
.btn-secondary { @apply btn bg-white/70 dark:bg-slate-800/50 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 ring-1 ring-black/5 dark:ring-white/10 shadow-sm; }
.form-input, .form-select { @apply w-full px-3.5 py-2.5 text-sm rounded-xl bg-white dark:bg-slate-800/50 border border-slate-300 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500; }
.form-label { @apply block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5; }
.animate-spin { animation: spin 1s linear infinite; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
</style>
